import React from "react";
import { calculateSingleSideCoatingBuildUpMm } from "../domain/coatingBuildUp";
import type {
  InputSummarySection,
  PipeSpecification,
  ProcessSpecification,
} from "../models/types";

type GeometryTile = {
  label: string;
  value: string;
  unit?: string;
};

const TILE_DEFS: Array<{ label: string; matchers: string[] }> = [
  { label: "Steel OD", matchers: ["steel od"] },
  { label: "Coating build-up", matchers: ["coating build"] },
  { label: "Total coating OD min", matchers: ["total coating od min", "coating od min"] },
  { label: "Total coating OD max", matchers: ["total coating od max", "coating od max"] },
  { label: "Max blasting bandwidth", matchers: ["max blasting bandwidth", "blasting bandwidth"] },
  { label: "Max tops of chamfers", matchers: ["max tops of chamfer", "tops of chamfer"] },
];

const normalize = (value: string) => value.trim().toLowerCase();

const findSummaryValue = (sections: InputSummarySection[] | undefined, matchers: string[]) => {
  if (!sections?.length) return null;

  const ranked = [...sections].sort((a, b) => {
    const score = (section: string) => {
      const key = normalize(section);
      if (key.includes("derived")) return 0;
      if (key.includes("geometry")) return 1;
      return 2;
    };
    return score(a.section) - score(b.section);
  });

  for (const section of ranked) {
    for (const item of section.values) {
      const label = normalize(item.label);
      if (matchers.some((matcher) => label.includes(matcher))) {
        return item.value?.trim() || null;
      }
    }
  }
  return null;
};

const withUnit = (value: string | undefined, unit: string | undefined) => {
  const v = value?.trim();
  if (!v) return null;
  return unit ? `${v} ${unit}` : v;
};

const splitMeasure = (raw: string): { value: string; unit?: string } => {
  const trimmed = raw.trim();
  const match = trimmed.match(/^(-?\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (!match) return { value: trimmed };
  const [, number, rest] = match;
  return rest ? { value: number, unit: rest } : { value: number };
};

export const buildPipeGeometryTiles = (
  pipe: PipeSpecification,
  process: ProcessSpecification,
  inputSummary?: InputSummarySection[],
): GeometryTile[] => {
  const calculatedBuildUp = calculateSingleSideCoatingBuildUpMm(process, pipe);
  const fallbacks: Record<string, string | null> = {
    "Steel OD": withUnit(pipe.outsideDiameter, pipe.outsideDiameterUnit),
    "Coating build-up":
      calculatedBuildUp != null ? `${calculatedBuildUp} mm` : null,
  };

  return TILE_DEFS.map((def) => {
    const fromSummary = findSummaryValue(inputSummary, def.matchers);
    const raw = fromSummary || fallbacks[def.label] || "—";
    if (raw === "—") return { label: def.label, value: "—" };
    const parsed = splitMeasure(raw);
    return { label: def.label, value: parsed.value, unit: parsed.unit };
  });
};

const PipeGeometryTiles = ({
  pipe,
  process,
  inputSummary,
}: {
  pipe: PipeSpecification;
  process: ProcessSpecification;
  inputSummary?: InputSummarySection[];
}) => {
  const tiles = buildPipeGeometryTiles(pipe, process, inputSummary);

  return (
    <>
      <p className="op-section-label">Pipe Geometry</p>
      <div className="op-stat-tile-grid op-stat-tile-grid--geometry">
        {tiles.map((tile) => (
          <div className="op-stat-tile" key={tile.label}>
            <div className="op-stat-tile__lbl">{tile.label}</div>
            <div className="op-stat-tile__val">
              {tile.value}
              {tile.unit ? <span className="op-stat-tile__unit"> {tile.unit}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PipeGeometryTiles;
